/* ── DISTRICT 21 — Theme JS ─────────────────────────────────────────────────── */

const D21 = window.D21 || {};

/* ── Utilities ──────────────────────────────────────────────────────────────── */
function qs(sel, ctx = document) { return ctx.querySelector(sel); }
function qsa(sel, ctx = document) { return [...ctx.querySelectorAll(sel)]; }

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return `EGP ${amount}`;
}

/* ── Cart API ───────────────────────────────────────────────────────────────── */
const CartAPI = {
  async get() {
    const r = await fetch('/cart.js');
    return r.json();
  },
  async add(variantId, quantity = 1, properties = {}) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    const data = await r.json();
    if (!r.ok) throw data;
    return data;
  },
  async change(key, quantity) {
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return r.json();
  }
};

/* ── Cart Drawer ────────────────────────────────────────────────────────────── */
const CartDrawer = {
  drawer: null,
  overlay: null,
  body: null,
  footer: null,
  FREE_SHIP_CENTS: 15000,

  init() {
    this.drawer  = qs('#cart-drawer');
    this.overlay = qs('#cart-overlay');
    this.body    = qs('#cart-drawer-body');
    this.footer  = qs('#cart-drawer-footer');

    qs('#cart-close-btn')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('click', e => {
      if (e.target.matches('[data-cart-open]')) this.open();
    });

    this.bindCartEvents();
  },

  open() {
    this.drawer?.setAttribute('aria-hidden', 'false');
    this.overlay?.setAttribute('aria-hidden', 'false');
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    this.drawer?.setAttribute('aria-hidden', 'true');
    this.overlay?.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  },

  async refresh() {
    const cart = await CartAPI.get();
    this.render(cart);
    this.updateCount(cart.item_count);
    this.updateThreshold(cart.total_price);
    this.updateFooter(cart);
  },

  updateCount(count) {
    qsa('[data-cart-count]').forEach(el => {
      el.textContent = String(count).padStart(2, '0');
    });
    qs('#cart-count-drawer')?.textContent && (qs('#cart-count-drawer').textContent = String(count).padStart(2, '0'));
    const bubbles = qsa('.cart-count-bubble');
    bubbles.forEach(b => {
      b.textContent = count;
      b.style.display = count > 0 ? 'flex' : 'none';
    });
  },

  updateThreshold(totalCents) {
    const bar      = qs('#cart-threshold-bar');
    const textEl   = qs('#cart-threshold-text');
    const fillEl   = qs('#cart-threshold-fill');
    const pct      = Math.min(100, (totalCents / this.FREE_SHIP_CENTS) * 100);
    const remaining = Math.max(0, this.FREE_SHIP_CENTS - totalCents);

    if (fillEl) fillEl.style.width = pct + '%';
    if (textEl) {
      textEl.textContent = remaining === 0
        ? '✓ FREE SHIPPING UNLOCKED'
        : `${formatMoney(remaining)} AWAY FROM FREE SHIPPING`;
    }
    bar?.classList.toggle('is-unlocked', remaining === 0);
  },

  updateFooter(cart) {
    const footer = qs('#cart-drawer-footer');
    if (!footer) return;
    footer.style.display = cart.item_count > 0 ? 'block' : 'none';
    const subtotalEl = qs('#cart-subtotal-display');
    const totalEl    = qs('#cart-total-display');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (totalEl)    totalEl.textContent    = formatMoney(cart.total_price);
    const checkoutBtn = qs('#cart-checkout-btn');
    if (checkoutBtn) checkoutBtn.href = '/checkout';
  },

  render(cart) {
    if (!this.body) return;
    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <p class="font-display">YOUR CART IS EMPTY</p>
          <a href="/collections/all" class="btn btn--primary">SHOP ALL →</a>
        </div>`;
      return;
    }
    this.body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__img-wrap">
          ${item.image
            ? `<img src="${item.image}" alt="${item.title}" loading="lazy" width="72" height="72">`
            : `<div class="cart-item__img-placeholder">${item.product_title.slice(0,2).toUpperCase()}</div>`
          }
        </a>
        <div class="cart-item__info">
          <div class="cart-item__name">${item.product_title} — ${item.variant_options[0]}</div>
          <div class="cart-item__size font-mono">SIZE ${(item.variant_title || '').toUpperCase()}</div>
          <div class="cart-item__controls">
            <div class="qty-control">
              <button class="qty-btn btn-reset" data-action="decrease" data-key="${item.key}" aria-label="Decrease">−</button>
              <span class="qty-value font-mono">${item.quantity}</span>
              <button class="qty-btn btn-reset" data-action="increase" data-key="${item.key}" aria-label="Increase">+</button>
            </div>
            <div class="cart-item__right">
              <button class="cart-item__remove btn-reset font-mono" data-key="${item.key}">REMOVE</button>
              <span class="cart-item__price">${formatMoney(item.line_price)}</span>
            </div>
          </div>
        </div>
      </div>`).join('');
    this.bindCartEvents();
  },

  bindCartEvents() {
    // Qty buttons
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.qty-btn');
      if (!btn) return;
      const key    = btn.dataset.key;
      const action = btn.dataset.action;
      const item   = qs(`.cart-item[data-key="${key}"]`);
      const qtyEl  = item?.querySelector('.qty-value');
      if (!qtyEl) return;
      const current = parseInt(qtyEl.textContent, 10);
      const newQty  = action === 'increase' ? current + 1 : Math.max(1, current - 1);
      await CartAPI.change(key, newQty);
      await this.refresh();
    });

    // Remove buttons
    document.addEventListener('click', async e => {
      const btn = e.target.closest('.cart-item__remove');
      if (!btn) return;
      await CartAPI.change(btn.dataset.key, 0);
      await this.refresh();
    });
  }
};

/* ── Mobile Nav ─────────────────────────────────────────────────────────────── */
const MobileNav = {
  init() {
    const btn  = qs('#hamburger-btn');
    const menu = qs('#mobile-menu');
    if (!btn || !menu) return;
    btn.addEventListener('click', () => {
      const isOpen = menu.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', isOpen);
      btn.textContent = isOpen ? '✕' : '☰';
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });
    qsa('.mobile-menu__link', menu).forEach(link => {
      link.addEventListener('click', () => {
        menu.classList.remove('is-open');
        btn.textContent = '☰';
        document.body.style.overflow = '';
      });
    });
  }
};

/* ── PDP (Product Detail Page) ──────────────────────────────────────────────── */
const PDP = {
  product: null,
  selectedVariant: null,

  init() {
    const dataEl = qs('#product-json');
    if (!dataEl) return;
    this.product = JSON.parse(dataEl.textContent);
    this.selectedVariant = this.product.variants[0];

    this.initGallery();
    this.initSizePicker();
    this.initATC();
  },

  initGallery() {
    const slides  = qs('#pdp-slides');
    const dots    = qsa('.pdp__dot');
    const prevBtn = qs('#pdp-prev');
    const nextBtn = qs('#pdp-next');
    if (!slides) return;

    const total = slides.children.length;
    let current = 0;

    const goTo = (index) => {
      current = (index + total) % total;
      slides.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    };

    prevBtn?.addEventListener('click', () => goTo(current - 1));
    nextBtn?.addEventListener('click', () => goTo(current + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

    // Touch / swipe
    let touchStartX = 0;
    const carousel = qs('#pdp-carousel');
    carousel?.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    carousel?.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) goTo(diff > 0 ? current + 1 : current - 1);
    });

    // Keyboard
    document.addEventListener('keydown', e => {
      if (!qs('#pdp-carousel')) return;
      if (e.key === 'ArrowLeft')  goTo(current - 1);
      if (e.key === 'ArrowRight') goTo(current + 1);
    });
  },

  initSizePicker() {
    const sizeBtns = qsa('.pdp__size-btn');
    const sizeLabel = qs('#pdp-size-label');
    const atcBtn    = qs('#pdp-atc-btn');
    const buyNowBtn = qs('#pdp-buy-now-btn');

    sizeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        sizeBtns.forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        const variantId = btn.dataset.variantId;
        this.selectedVariant = this.product.variants.find(v => String(v.id) === variantId);

        if (sizeLabel) {
          sizeLabel.textContent = `SIZE / ${btn.textContent.trim()}`;
          sizeLabel.classList.add('has-selection');
        }
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.textContent = `ADD TO CART — ${formatMoney(this.selectedVariant.price)}`;
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = false;
          buyNowBtn.textContent = 'BUY NOW →';
        }
      });
    });
  },

  initATC() {
    const atcBtn    = qs('#pdp-atc-btn');
    const buyNowBtn = qs('#pdp-buy-now-btn');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', async () => {
      if (!this.selectedVariant) return;
      atcBtn.disabled = true;
      atcBtn.textContent = 'ADDING...';

      try {
        await CartAPI.add(this.selectedVariant.id, 1);
        atcBtn.classList.add('is-added');
        atcBtn.textContent = '✓ ADDED TO CART';
        await CartDrawer.refresh();
        CartDrawer.open();
        setTimeout(() => {
          atcBtn.classList.remove('is-added');
          atcBtn.disabled = false;
          atcBtn.textContent = `ADD TO CART — ${formatMoney(this.selectedVariant.price)}`;
        }, 2200);
      } catch (err) {
        atcBtn.disabled = false;
        atcBtn.textContent = 'ERROR — TRY AGAIN';
        setTimeout(() => {
          atcBtn.textContent = `ADD TO CART — ${formatMoney(this.selectedVariant.price)}`;
        }, 2000);
      }
    });

    if (!buyNowBtn) return;
    buyNowBtn.addEventListener('click', async () => {
      if (!this.selectedVariant) return;
      buyNowBtn.disabled = true;
      buyNowBtn.textContent = 'PROCESSING...';
      try {
        await CartAPI.add(this.selectedVariant.id, 1);
        window.location.href = '/checkout';
      } catch (err) {
        buyNowBtn.disabled = false;
        buyNowBtn.textContent = 'BUY NOW →';
      }
    });
  }
};

/* ── Quick Add ──────────────────────────────────────────────────────────────── */
document.addEventListener('click', async e => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn) return;
  e.preventDefault();
  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  const origText = btn.textContent;
  btn.textContent = '...';
  btn.disabled = true;
  try {
    await CartAPI.add(parseInt(variantId, 10), 1);
    await CartDrawer.refresh();
    CartDrawer.open();
  } catch {}
  btn.textContent = origText;
  btn.disabled = false;
});

/* ── Shop Filters (Collection page) ────────────────────────────────────────── */
const ShopFilters = {
  init() {
    // Mobile filter toggle
    const filterBtn   = qs('#filter-toggle-btn');
    const filterPanel = qs('#mobile-filter-panel');
    if (filterBtn && filterPanel) {
      filterBtn.addEventListener('click', () => {
        const isOpen = filterPanel.classList.toggle('is-open');
        filterBtn.classList.toggle('is-active', isOpen);
        filterBtn.textContent = isOpen ? 'FILTER −' : 'FILTER +';
      });
    }
    // Size filter buttons (URL-based)
    qsa('.size-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = new URL(window.location.href);
        const currentTag = url.searchParams.get('filter.p.m.custom.size');
        const size = btn.dataset.size;
        if (currentTag === size) {
          url.searchParams.delete('filter.p.m.custom.size');
        } else {
          url.searchParams.set('filter.p.m.custom.size', size);
        }
        window.location.href = url.href;
      });
    });
  }
};

/* ── Cart Toggle (header cart button) ──────────────────────────────────────── */
document.addEventListener('click', e => {
  if (e.target.closest('#cart-toggle-btn')) {
    CartDrawer.open();
  }
});

/* ── FAQ Accordion (Contact page) ──────────────────────────────────────────── */
const FAQ = {
  init() {
    qsa('.contact-faq__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const item   = btn.closest('.contact-faq__item');
        const answer = item.querySelector('.contact-faq__a');
        const icon   = btn.querySelector('.contact-faq__icon');
        const isOpen = answer.style.display === 'block';
        // Close all
        qsa('.contact-faq__a').forEach(a => a.style.display = 'none');
        qsa('.contact-faq__icon').forEach(i => i.classList.remove('is-open'));
        // Toggle current
        if (!isOpen) {
          answer.style.display = 'block';
          icon?.classList.add('is-open');
        }
      });
    });
  }
};

/* ── Init ───────────────────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  MobileNav.init();
  PDP.init();
  ShopFilters.init();
  FAQ.init();

  // Close cart when navigating to checkout
  qs('#cart-checkout-btn')?.addEventListener('click', () => CartDrawer.close());

  // Cart shop link
  qs('#cart-shop-link')?.addEventListener('click', () => CartDrawer.close());
});
